
import React from "react";
import { Image, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { AspectRatio } from "@/components/ui/aspect-ratio";

interface PostMetadataFeaturedImageProps {
  featuredImage: string;
  setFeaturedImage: (url: string) => void;
  featuredImageAlt: string;
  setFeaturedImageAlt: (alt: string) => void;
  featuredImageTitle: string;
  setFeaturedImageTitle: (title: string) => void;
  onFileUpload?: (file: File) => Promise<string>;
}

const PostMetadataFeaturedImage: React.FC<PostMetadataFeaturedImageProps> = ({
  featuredImage,
  setFeaturedImage,
  featuredImageAlt,
  setFeaturedImageAlt,
  featuredImageTitle,
  setFeaturedImageTitle,
  onFileUpload,
}) => {
  // Handle image upload
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && onFileUpload) {
      try {
        const url = await onFileUpload(e.target.files[0]);
        setFeaturedImage(url);
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };

  return (
    <div className="space-y-2">
      <Label>Featured Image (16:9 recommended)</Label>
      <div className="border rounded-md p-4 bg-gray-50">
        {featuredImage ? (
          <div className="space-y-3">
            <AspectRatio ratio={16/9} className="overflow-hidden rounded-md">
              <img 
                src={featuredImage} 
                alt={featuredImageAlt || "Featured post image"} 
                className="w-full h-full object-cover"
              />
            </AspectRatio>
            
            <div className="space-y-3 mt-3">
              <div>
                <Label htmlFor="featured-image-alt" className="text-sm">Alt Text</Label>
                <Input
                  id="featured-image-alt"
                  value={featuredImageAlt}
                  onChange={(e) => setFeaturedImageAlt(e.target.value)}
                  placeholder="Describe the image for accessibility and SEO"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="featured-image-title" className="text-sm">Title Text</Label>
                <Input
                  id="featured-image-title"
                  value={featuredImageTitle}
                  onChange={(e) => setFeaturedImageTitle(e.target.value)}
                  placeholder="Title shown when hovering over the image"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="flex justify-between">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setFeaturedImage("")}
              >
                Remove
              </Button>
              <div className="flex items-center">
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => document.getElementById("image-upload")?.click()}
                >
                  Change <Upload size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center bg-gray-100 rounded-md">
            <AspectRatio ratio={16/9} className="w-full">
              <div className="flex flex-col items-center justify-center h-full">
                <Image size={48} className="text-gray-400 mb-2" />
                <p className="text-sm text-gray-500 mb-3">No featured image selected (16:9 recommended)</p>
              </div>
            </AspectRatio>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
            <Button 
              type="button" 
              variant="outline"
              onClick={() => document.getElementById("image-upload")?.click()}
              className="mt-4"
            >
              Upload Image
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostMetadataFeaturedImage;
